import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Droplet, Calculator, Info } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './PipeFlowCalculatorPage/components/CalculationForm';
import ResultsOverview from './PipeFlowCalculatorPage/components/ResultsOverview';
import FlowRateVisualization from './PipeFlowCalculatorPage/components/FlowRateVisualization';
import QuickAdjustmentSidebar from './PipeFlowCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './PipeFlowCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './PipeFlowCalculatorPage/components/TipsSidebar';
import InfoContent from './PipeFlowCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  pipeType: 'steel',             // Тип трубы
  pipeInnerDiameter: '40',       // Внутренний диаметр трубы (мм)
  pipeLength: '100',             // Длина трубы (м)
  pipeRoughness: '0.15',         // Шероховатость трубы (мм)
  systemPressure: '4',           // Давление в системе (бар)
  waterTemperature: '15',        // Температура воды (°C)
  calculationType: 'flowRate',   // Тип расчета (flowRate - расход, pressure - давление, diameter - диаметр)
  
  // Параметры для расчета расхода
  pressureDrop: '0.5',           // Перепад давления (бар) - для расчета расхода
  
  // Параметры для расчета давления
  flowRate: '2.5',               // Расход воды (м³/ч) - для расчета перепада давления
  
  // Параметры для расчета диаметра
  requiredFlowRate: '3.0',       // Требуемый расход воды (м³/ч) - для расчета диаметра
  maxPressureDrop: '0.8',        // Максимальный перепад давления (бар) - для расчета диаметра
  
  // Дополнительные параметры
  includeLocalResistance: false, // Учитывать местные сопротивления
  bendCount: '4',                // Количество поворотов 90°
  valveCount: '2',               // Количество вентилей
  teeCount: '1',                 // Количество тройников
  entranceType: 'standard',      // Тип входа в трубу
  exitType: 'standard',          // Тип выхода из трубы
  
  includeElevationChange: false, // Учитывать изменение высоты
  elevationChange: '2',          // Разница в высоте (м, положительное - вверх)
};

// Определение типов труб с характеристиками
export const pipeTypes = [
  { id: 'steel', name: 'Стальная', defaultRoughness: 0.15, densityFactor: 1.0 },
  { id: 'copper', name: 'Медная', defaultRoughness: 0.0015, densityFactor: 0.98 },
  { id: 'pvc', name: 'ПВХ (пластиковая)', defaultRoughness: 0.0070, densityFactor: 0.97 },
  { id: 'pp', name: 'Полипропиленовая', defaultRoughness: 0.0040, densityFactor: 0.96 },
  { id: 'pex', name: 'Сшитый полиэтилен (PEX)', defaultRoughness: 0.0030, densityFactor: 0.95 },
  { id: 'hdpe', name: 'ПНД (полиэтилен низкого давления)', defaultRoughness: 0.0070, densityFactor: 0.97 },
  { id: 'cast_iron', name: 'Чугунная', defaultRoughness: 0.26, densityFactor: 1.02 },
  { id: 'stainless_steel', name: 'Нержавеющая сталь', defaultRoughness: 0.0015, densityFactor: 1.0 },
];

// Типы входов/выходов
export const connectionTypes = [
  { id: 'standard', name: 'Стандартный', factor: 0.5 },
  { id: 'sharp', name: 'Острый', factor: 1.0 },
  { id: 'rounded', name: 'Закругленный', factor: 0.2 },
  { id: 'protruding', name: 'Выступающий', factor: 1.0 },
  { id: 'conical', name: 'Конический', factor: 0.3 },
];

// Стандартные диаметры труб (мм)
export const standardDiameters = {
  steel: [15, 20, 25, 32, 40, 50, 65, 80, 100, 125, 150, 200, 250, 300],
  copper: [10, 12, 15, 18, 22, 28, 35, 42, 54, 67, 76, 108],
  pvc: [16, 20, 25, 32, 40, 50, 63, 75, 90, 110, 125, 140, 160, 200, 225, 250, 315],
  pp: [16, 20, 25, 32, 40, 50, 63, 75, 90, 110],
  pex: [16, 20, 25, 32],
  hdpe: [20, 25, 32, 40, 50, 63, 75, 90, 110, 125, 140, 160, 180, 200, 225, 250, 280, 315],
  cast_iron: [50, 80, 100, 150, 200, 250, 300, 350, 400, 500, 600],
  stainless_steel: [12, 15, 18, 22, 28, 35, 42, 54, 76, 108]
};

/**
 * PipeFlowCalculatorPage Component - Калькулятор расхода воды в трубах.
 */
const PipeFlowCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('pipe-flow-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор расхода воды в трубах | Gensio';
  const metaDescription = tool?.metaDescription || 'Расчет расхода воды, перепада давления и оптимального диаметра труб для водопроводных систем с учетом материала, длины, шероховатости и местных сопротивлений.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalcsData = localStorage.getItem('savedPipeCalculations');
    if (savedCalcsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalcsData));
      } catch (e) {
        console.error("Error loading saved calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'flow-rate':
          newState.calculationType = 'flowRate';
          break;
        case 'pressure-drop':
          newState.calculationType = 'pressure';
          break;
        case 'diameter':
          newState.calculationType = 'diameter';
          break;
        case 'hot-water':
          newState.waterTemperature = '60';
          break;
        case 'cold-water':
          newState.waterTemperature = '10';
          break;
        case 'steel-pipes':
          newState.pipeType = 'steel';
          break;
        case 'plastic-pipes':
          newState.pipeType = 'pvc';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalc = {
      id: Date.now().toString(),
      name: name || `Расчет для ${pipeTypes.find(t => t.id === formData.pipeType)?.name} трубы D${formData.pipeInnerDiameter}мм`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      calculationType: formData.calculationType
    };

    const updatedSavedCalcs = [...savedCalculations, newSavedCalc];
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedPipeCalculations', JSON.stringify(updatedSavedCalcs));

    return newSavedCalc;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalcs = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedPipeCalculations', JSON.stringify(updatedSavedCalcs));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calcToLoad = savedCalculations.find(calc => calc.id === id);
    if (calcToLoad) {
      setFormData(calcToLoad.formData);
      setResults(calcToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.blue.100)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.blue.900)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Droplet className="w-8 h-8 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор расхода воды в трубах'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              pipeTypes={pipeTypes}
              connectionTypes={connectionTypes}
              standardDiameters={standardDiameters}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <FlowRateVisualization 
                  results={results} 
                  formData={formData}
                />
              </>
            )}

            {/* --- Информационный контент о расчете труб --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              standardDiameters={standardDiameters}
            />

            {/* --- Панель сравнения расчетов --- */}
            <ComparisonSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default PipeFlowCalculatorPage;